import { UIView } from "uicore-ts"
import { RHCore } from "../../Core/RHCore"
import { RHConflictOfInterestReasonCode, RHConflictOfInterestResult } from "../../SocketClientFunctions"
import { RHAlertBannerView } from "./RHAlertBannerView"
import { RHTextSanitizer } from "./Utils_slop/RHTextSanitizer"


export interface RHConflictOfInterestQuery {
    procurementId: string
    fromSide: "procurement" | "offer"
    offerId?: string
}


// Translates the backend conflict engine's stable reason CODES
// (controllers/services/ConflictOfInterestService.ts) into the Estonian sentences this UI
// otherwise speaks. Keyed by code, not by the backend's own (English, log-facing) sentence text -
// a wording change there can never silently break translation here, unlike the previous
// exact-English-sentence-matching version of this table.
export const RHConflictOfInterestReasonTranslations: Record<RHConflictOfInterestReasonCode, string> = {
    procurement_and_offer_team_access:
        "Sama kasutajakonto osaleb sellel hankel nii hanke meeskonnas kui pakkuja poolel.",
    confidential_offers_for_competing_companies:
        "Sul on konfidentsiaalne ligipääs mitme omavahel konkureeriva pakkuja pakkumusele samas hankes."
}


export function RHConflictOfInterestWarningText(result: RHConflictOfInterestResult): string {
    let text = "Sul on tuvastatud võimalik huvide konflikt selle hankega: " +
        result.reasons.map(reason => RHConflictOfInterestReasonTranslations[reason] ?? reason).join(" ")
    if (result.hasDisclosure) {
        text += " Oled konflikti juba deklareerinud."
    }
    text += " See on ainult hoiatus ega takista pakkujaks registreerumist, pakkumuse esitamist või hanke meeskonnas osalemist."
    return text
}


// Shows server-detected conflicts separately from the personal no-conflict confirmation.
export class RHConflictOfInterestBannerView extends UIView {

    readonly banner = new RHAlertBannerView()

    _query?: RHConflictOfInterestQuery
    _result?: RHConflictOfInterestResult

    async load(query: RHConflictOfInterestQuery) {
        this._query = query
        this._applyResult(undefined)
        try {
            const result = await RHCore.sharedInstance.procurementStore.retrieveConflictOfInterestStatus(
                query.procurementId
            )
            if (this._query !== query) {
                return
            }
            this._applyResult(result)
        }
        catch (error) {
            if (this._query !== query) {
                return
            }
            console.error("Conflict-of-interest status load error:", error)
            this._applyResult(undefined)
        }
    }

    /** Resets to the not-applicable state, e.g. when the owning screen moves to a procurement/offer this banner doesn't cover. */
    clear() {
        this._query = undefined
        this._applyResult(undefined)
    }

    get isVisible(): boolean {
        return this._result?.status === "required" ||
            this._result?.status === "disclosed" ||
            this._result?.status === "blocked" ||
            this._result?.status === "approved_exception"
    }

    get status(): RHConflictOfInterestResult["status"] | undefined {
        return this._result?.status
    }

    _applyResult(result: RHConflictOfInterestResult | undefined) {
        this._result = result
        this._reload()
        this.setNeedsLayoutUpToRootView()
    }

    _reload() {
        this.banner.removeFromSuperview()

        const result = this._result
        if (!result) {
            return
        }

        if (result.status === "required" || result.status === "disclosed") {
            this.banner.configuredWithObject({
                alertType: RHAlertBannerView.type.Warning,
                text: this._bannerTextForConflict(result)
            })
            this.banner.addedAsSubviewToView(this)
        }
        else if (result.status === "blocked") {
            this.banner.configuredWithObject({
                alertType: RHAlertBannerView.type.Warning,
                text: this._bannerTextForBlockedConflict(result)
            })
            this.banner.addedAsSubviewToView(this)
        }
        else if (result.status === "approved_exception") {
            this.banner.configuredWithObject({
                alertType: RHAlertBannerView.type.Success,
                text: this._bannerTextForApprovedException(result)
            })
            this.banner.addedAsSubviewToView(this)
        }
    }

    _translatedReasons(result: RHConflictOfInterestResult): string[] {
        return result.reasons.map(reason => RHConflictOfInterestReasonTranslations[reason] ?? reason)
    }

    _bannerTextForConflict(result: RHConflictOfInterestResult): string {
        return RHConflictOfInterestWarningText(result)
    }

    _bannerTextForBlockedConflict(result: RHConflictOfInterestResult): string {
        const currentOffer = result.competingOffers?.find(offer => offer.offerId === this._query?.offerId)
        const currentCompanyId = currentOffer?.offererCompanyId ??
            RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        const competingOffers = (result.competingOffers ?? []).filter(offer =>
            this._query?.fromSide !== "offer" || offer.offererCompanyId !== currentCompanyId
        )
        if (competingOffers.length > 0) {
            return competingOffers.map(offer => {
                const companyName = RHTextSanitizer.escapedHTML(offer.offererCompanyName || "-")
                let offerDescription = "Pakkumus on pooleli ja selle vastutav kasutaja on <strong>" +
                    RHTextSanitizer.escapedHTML(offer.responsibleUserEmail || "-") + "</strong>"
                if (offer.status === "submitted") {
                    offerDescription = "Pakkumus on kasutaja <strong>" +
                        RHTextSanitizer.escapedHTML(offer.submittedByEmail || "-") + "</strong> poolt esitatud"
                }
                return `Sa oled juba ettevõtte <strong>${companyName}</strong> sama hanke pakkuja meeskonnas. ` +
                    offerDescription + ", mistõttu Sa ei saa pakkumust esitada."
            }).join("<br><br>")
        }
        let text = "Sul on tuvastatud blokeeriv huvide konflikt selle hankega: " +
            this._translatedReasons(result).join(" ")
        if (result.hasDisclosure) {
            text += " Oled konflikti juba deklareerinud."
        }
        text += " Konflikti lahendamiseni ei saa pakkumust esitada."
        return text
    }

    _bannerTextForApprovedException(result: RHConflictOfInterestResult): string {
        return "Sinu huvide konfliktile on antud erand ja ligipääs on jätkuvalt lubatud: " +
            this._translatedReasons(result).join(" ")
    }

    override layoutSubviews() {
        super.layoutSubviews()

        const bounds = this.contentBoundsWithInset(0)
        bounds.rectangleWithHeight(this.banner)
            .assignedAsFrameOfView(this.banner)
    }

    override intrinsicContentHeight(constrainingWidth: number): number {
        if (!this.isVisible) {
            return 0
        }
        return this.banner.intrinsicContentHeight(constrainingWidth)
    }

}
